import { useRef, useState } from "react";
import { Upload, X, Loader2, Link2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { adminApi, formatApiError } from "@/lib/api";
import { SafeImage } from "@/components/SafeImage";
import { toast } from "sonner";

const MAX_DIM = 1800;

async function compressImage(file) {
  if (file.type === "image/gif") return file;
  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, MAX_DIM / Math.max(bitmap.width, bitmap.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);
    canvas.getContext("2d").drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise((r) => canvas.toBlob(r, "image/jpeg", 0.85));
    return blob ? new File([blob], "photo.jpg", { type: "image/jpeg" }) : file;
  } catch {
    return file;
  }
}

export const ImageUploadField = ({ label = "Photo / Flyer", value, onChange, testid }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [broken, setBroken] = useState(false);
  const [showUrl, setShowUrl] = useState(false);

  async function handleFile(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const body = new FormData();
      body.append("file", await compressImage(file));
      const { data } = await adminApi.post("/admin/uploads/image", body, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
      toast.success("Photo uploaded");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  }

  return (
    <div data-testid={testid}>
      <Label className="text-xs tracking-widest uppercase">{label}</Label>
      <div className="mt-2 flex gap-3 items-start">
        {value ? (
          <div className="relative w-28 h-28 shrink-0 border border-[var(--trio-border)]">
            <SafeImage src={value} alt="Preview" className="w-full h-full object-cover" testid={`${testid}-preview`} onStatus={(s) => setBroken(s === "error")} />
            <button type="button" onClick={() => onChange("")} data-testid={`${testid}-remove`} className="absolute -top-2 -right-2 bg-black text-white p-1 rounded-full" title="Remove photo">
              <X className="w-3 h-3" />
            </button>
          </div>
        ) : null}
        <div className="flex-1 space-y-2">
          <input ref={inputRef} type="file" accept="image/*" onChange={handleFile} className="hidden" data-testid={`${testid}-input`} />
          <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading} data-testid={`${testid}-upload-btn`} className="w-full inline-flex items-center justify-center gap-2 border-2 border-dashed border-[var(--trio-border)] hover:border-[var(--trio-primary)] hover:text-[var(--trio-primary)] py-4 text-xs uppercase tracking-widest font-bold transition-colors disabled:opacity-60">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {uploading ? "Uploading…" : value ? "Replace Photo" : "Upload From Phone / Computer"}
          </button>
          <button type="button" onClick={() => setShowUrl((s) => !s)} data-testid={`${testid}-toggle-url`} className="inline-flex items-center gap-1 text-[11px] text-[var(--trio-muted)] hover:underline">
            <Link2 className="w-3 h-3" /> {showUrl ? "Hide link option" : "Or paste an image link"}
          </button>
          {showUrl && (
            <Input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="https://…/photo.jpg" data-testid={`${testid}-url`} className="rounded-none border-[var(--trio-border)] bg-[var(--trio-bg)]" />
          )}
          {value && broken && (
            <p data-testid={`${testid}-broken-warning`} className="text-[11px] text-red-700">
              That link isn't a direct image (e.g. Facebook/Google Photos pages won't work). Upload the photo instead.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};
